import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, useActionData, useNavigation, useMatches } from 'react-router';

import { PlatformRole } from '~/common_entities/platform_role';
import { isValidEmail } from '~/core/utils/email_validator';
import { Drawer } from '~/shared/common/Drawer';
import { CustomButton } from '~/shared/components/custom_button';
import { CustomInput } from '~/shared/components/custom_input_field';
import { CustomSelect } from '~/shared/components/custom_select';

interface InviteUserDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  platformRoles: PlatformRole[];
  onSuccess?: () => void;
}

interface FormValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

// Extracted validation helper functions
function validateFirstName(firstName: string, t: any): FormValidationResult {
  if (!firstName) {
    return { isValid: false, errorMessage: t('firstNameRequired') };
  }
  return { isValid: true };
}

function validateLastName(lastName: string, t: any): FormValidationResult {
  if (!lastName) {
    return { isValid: false, errorMessage: t('lastNameRequired') };
  }
  return { isValid: true };
}

function validatePhoneNumberRequired(phoneNumber: string, t: any): FormValidationResult {
  if (!phoneNumber) {
    return { isValid: false, errorMessage: t('phoneNumberRequired') };
  }
  return { isValid: true };
}

function validatePhoneNumberFormat(
  phoneNumber: string,
  t: any,
  regexPattern?: string
): FormValidationResult {
  if (!regexPattern) {
    // Fallback length validation
    if (phoneNumber.length < 8 || phoneNumber.length > 15) {
      return { isValid: false, errorMessage: t('invalidPhoneNumberLength') };
    }
    return { isValid: true };
  }

  try {
    const cleanRegex = regexPattern.replace(/^['"]|['"]$/g, '');
    const phoneRegex = new RegExp(cleanRegex);

    if (!phoneRegex.test(phoneNumber)) {
      return { isValid: false, errorMessage: t('invalidPhoneNumberFormat') };
    }
    return { isValid: true };
  } catch (error) {
    console.warn('Invalid regex pattern for country:', regexPattern);
    // Fallback to length validation on regex error
    if (phoneNumber.length < 8 || phoneNumber.length > 15) {
      return { isValid: false, errorMessage: t('invalidPhoneNumberLength') };
    }
    return { isValid: true };
  }
}

function validateEmail(email: string, t: any): FormValidationResult {
  if (!email) {
    return { isValid: false, errorMessage: t('emailRequired') };
  }

  if (!isValidEmail(email)) {
    return { isValid: false, errorMessage: t('invalidEmailFormat') };
  }

  return { isValid: true };
}

function validatePlatformRole(platformRoleId: string, t: any): FormValidationResult {
  if (!platformRoleId) {
    return { isValid: false, errorMessage: t('platformRoleRequired') };
  }
  return { isValid: true };
}

// Extracted validation orchestration
function validateFormData(
  formData: FormData,
  t: any,
  currentCountryRegex?: string
): FormValidationResult {
  const firstName = formData.get('firstName') as string;
  const lastName = formData.get('lastName') as string;
  const phoneNumber = formData.get('phoneNumber') as string;
  const email = formData.get('email') as string;
  const platformRoleId = formData.get('platformRoleId') as string;

  // Validate in sequence with early returns
  const firstNameResult = validateFirstName(firstName, t);
  if (!firstNameResult.isValid) return firstNameResult;

  const lastNameResult = validateLastName(lastName, t);
  if (!lastNameResult.isValid) return lastNameResult;

  const phoneRequiredResult = validatePhoneNumberRequired(phoneNumber, t);
  if (!phoneRequiredResult.isValid) return phoneRequiredResult;

  const phoneFormatResult = validatePhoneNumberFormat(phoneNumber, t, currentCountryRegex);
  if (!phoneFormatResult.isValid) return phoneFormatResult;

  const emailResult = validateEmail(email, t);
  if (!emailResult.isValid) return emailResult;

  const roleResult = validatePlatformRole(platformRoleId, t);
  if (!roleResult.isValid) return roleResult;

  return { isValid: true };
}

// Custom hook for country matching logic
function useCurrentCountry(matches: any) {
  const rootData = matches.find((match: any) => match.id === 'root')?.data as any;
  const countries = rootData?.countries || [];
  const userMetadata = rootData?.userMetadata;
  const currentOrg = userMetadata?.organizations?.find((org: any) => org.isCurrent);

  const currentCountry = countries.find((c: any) => {
    const countryId = c.id;
    const orgCountryId = currentOrg?.countryId;

    return (
      countryId === orgCountryId ||
      String(countryId) === String(orgCountryId) ||
      Number(countryId) === Number(orgCountryId) ||
      countryId == orgCountryId
    );
  });

  return { currentCountry, countries };
}

// Custom hook for phone configuration
function usePhoneConfig(currentCountry: any) {
  const phoneRegexPattern =
    currentCountry?.regexForCountryMobileNumber?.replace(/^['"]|['"]$/g, '') || '[0-9-]+';

  const phoneMaxLength = (() => {
    const match = phoneRegexPattern.match(/\\d{(\d+)}/);
    return match ? Number.parseInt(match[1], 10) : undefined;
  })();

  return { phoneRegexPattern, phoneMaxLength };
}

export const InviteUserDrawer: React.FC<InviteUserDrawerProps> = ({
  isOpen,
  onClose,
  platformRoles,
  onSuccess,
}) => {
  const { t } = useTranslation(['userManagement', 'common']);
  const actionData = useActionData();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const matches = useMatches();

  const navigation = useNavigation();
  const _isSubmitting = navigation.state === 'submitting';
  const formSubmitted = useRef(false);

  // Use custom hooks
  const { currentCountry, countries: _countries } = useCurrentCountry(matches);
  const { phoneRegexPattern: _phoneRegexPattern, phoneMaxLength } = usePhoneConfig(currentCountry);

  // Format roles for select dropdown
  const roleOptions = platformRoles
    .filter((role) => role.isActive)
    .map((role) => ({
      value: role.id || '',
      label: role.platformRoleNameCaption || '',
    }));

  const handleSubmit = () => {
    setLoading(true);
    setErrorMsg('');

    if (!formRef.current) {
      setLoading(false);
      return;
    }

    const formData = new FormData(formRef.current);
    const validationResult = validateFormData(
      formData,
      t,
      currentCountry?.regexForCountryMobileNumber
    );

    if (!validationResult.isValid) {
      setErrorMsg(validationResult.errorMessage || 'Validation error');
      setLoading(false);
      return;
    }

    formSubmitted.current = true;
    formRef.current.submit();
  };

  // Call onSuccess callback when form submission is successful
  useEffect(() => {
    console.log({ actionData });
    if (actionData?.success) {
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    }
  }, [actionData, onClose, onSuccess]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow control keys
    if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
      return;
    }

    // Normalize key
    const key = e.key === '=' && e.shiftKey ? '+' : e.key;

    // Allow only numbers, +, -
    if (!/[0-9+-]/.test(key)) {
      e.preventDefault();
    }
  };

  const handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = e.target.value.replace(/[^0-9+-]/g, '');
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      position="right"
      width="30rem"
      title={t('inviteTitle', { ns: 'common' })}
      contentClassName="flex flex-col"
    >
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Email Invite Form */}
        <Form
          method="post"
          className="space-y-5"
          ref={formRef}
          onKeyDown={handleKeyDown}
        >
          <input
            type="hidden"
            name="actionType"
            value="inviteUser"
          />

          {/* Firstname & Lastname */}
          <div className="flex items-center gap-2 p-1">
            <div className="flex-1">
              <CustomInput
                placeholder={t('firstNamePlaceholderinvite', { ns: 'common' })}
                labelClassName="capitalize"
                label={t('firstName', { ns: 'common' })}
                name="firstName"
                type="text"
                autoComplete="given-name"
                height={48}
                labelType="required"
                className="border border-neutralLight"
              />
            </div>
            <div className="flex-1">
              <CustomInput
                placeholder={t('lastNamePlaceholderinvite', { ns: 'common' })}
                labelClassName="capitalize"
                label={t('lastName', { ns: 'common' })}
                name="lastName"
                type="text"
                autoComplete="given-name"
                height={48}
                labelType="required"
                className="border border-neutralLight"
              />
            </div>
          </div>

          <div className="flex-1 p-1">
            <CustomInput
              name="phoneNumber"
              label={t('phoneNumber', { ns: 'common' })}
              labelType="required"
              labelClassName="!text-fontColor capitalize"
              placeholder={t('phoneNumber', { ns: 'common' })}
              type="tel"
              pattern="[0-9+\-]+"
              maxLength={phoneMaxLength}
              onKeyDown={handlePhoneKeyDown}
              onInput={handlePhoneInput}
            />
          </div>

          {/* Email */}
          <div className="flex items-center p-1">
            <div className="flex-1">
              <CustomInput
                placeholder={t('enterYourEmail', { ns: 'common' })}
                label={t('emailAddress', { ns: 'common' })}
                labelClassName="capitalize"
                name="email"
                type="email"
                height={48}
                labelType="required"
                className="border border-neutralLight"
              />
            </div>
          </div>

          <div className="p-1">
            <CustomSelect
              label={t('role', { ns: 'common' })}
              name="platformRoleId"
              options={roleOptions}
              className="rounded-xl border border-neutralLight"
              labelType="required"
            />
          </div>

          {errorMsg && (
            <div className="bodyMd mb-6 w-full rounded-xl border border-l-4 border-btnColor border-l-btnColor bg-[#C6343414] px-4 py-2.5 text-fontColor">
              <div className="flex items-center justify-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-btnColor" />
                <p className="bodySm">{errorMsg}</p>
              </div>
            </div>
          )}

          {/* Hidden submit button to ensure form submission works */}
          <button
            type="submit"
            className="hidden"
          />
        </Form>
      </div>

      {/* Footer buttons pinned to bottom with padding */}
      <div className="mt-auto flex justify-between border-t border-neutralLight pt-4">
        <CustomButton
          dataName="send-invite-btn"
          buttonType="filled"
          variant="primary"
          type="button"
          onClick={handleSubmit}
          isLoading={loading}
          className="w-full"
        >
          {t('sendInvite', { ns: 'common' })}
        </CustomButton>
      </div>
    </Drawer>
  );
};
